import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import {
    CdkDrag,
    type CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    CdkDropListGroup,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom } from 'rxjs';

import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { arrayLengthEq } from '@common/helpers';

import { EControlHandleEvents } from '@ui/sp-control-handle/src/sp-control-handle.enum';
import { SpControlHandleComponent } from '@ui/sp-control-handle';

import { SpAnimationSettingsSignalComponent } from '@components/sp-animation-settings-signal';

import type { TabDataModel } from '@sites/entity-signal-form';

import type { NormalizedTabEntity } from '@web/types';

import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';
import { WebTabFormService } from '@web/core/services/web-tab-form.service';

import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';

import { SettingsBodyComponent } from '@web/shared/components/web-settings/settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '@web/shared/components/web-settings/settings-breadcrumbs/settings-breadcrumbs.component';

import { AbstractBaseSignalSettingsComponent } from '../abstract-base-settings/abstract-base-signal-settings.component';
import { TabAppearanceSettingsComponent } from './tab-appearance-settings/tab-appearance-settings.component';
import { TabItemSettingsComponent } from './tab-item-settings/tab-item-settings.component';

enum CurrentSettings {
    Main = 'main',
    Appearance = 'appearance',
    Tab = 'tab',
}

@Component({
    selector: 'web-tab-settings',
    templateUrl: './tab-settings.component.html',
    styleUrls: ['./tab-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        A11yModule,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        CdkDragHandle,
        TooltipModule,
        TranslatePipe,
        SpControlHandleComponent,
        SpAnimationSettingsSignalComponent,
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TabAppearanceSettingsComponent,
        TabItemSettingsComponent,
    ],
})
export class TabSettingsComponent extends AbstractBaseSignalSettingsComponent implements OnInit {
    public readonly entity = input.required<NormalizedTabEntity>();

    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly tabFormService = inject(WebTabFormService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly sectionsEntities = toSignal(this.sectionsFacade.sectionsEntities$);
    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$);

    public form!: ReturnType<WebTabFormService['getOrCreate']>;

    constructor() {
        super();
    }

    public ngOnInit(): void {
        this.form = this.tabFormService.getOrCreateScoped(this.entity().id, this.destroyRef);
    }

    public editableTabIndex = signal<number | null>(null);
    public currentSettings = signal<CurrentSettings>(CurrentSettings.Main);

    public readonly settingsEnum = CurrentSettings;

    private getActiveContourSidePosition() {
        const placementValue = this.form.placement().value();
        const activeContourSideValue = this.form.headerActiveContourSide().value();

        if (!activeContourSideValue) {
            return null;
        }

        if (placementValue === 'top') {
            return activeContourSideValue === 'top' ? 'outer' : 'inner';
        }

        if (placementValue === 'left') {
            return activeContourSideValue === 'left' ? 'outer' : 'inner';
        }

        if (placementValue === 'right') {
            return activeContourSideValue === 'right' ? 'outer' : 'inner';
        }

        return null;
    }

    public setPlacement(placement: TabDataModel['placement']): void {
        const activeContourSide = this.getActiveContourSidePosition();

        this.form.transact(() => {
            this.form.setPlacement(placement);

            if (activeContourSide) {
                const contourMap = {
                    top: activeContourSide === 'outer' ? 'top' : 'bottom',
                    left: activeContourSide === 'outer' ? 'left' : 'right',
                    right: activeContourSide === 'outer' ? 'right' : 'left',
                } as const;

                this.form.setHeaderActiveContourSide(contourMap[placement]);
            }
        });
    }

    public dropGroup(event: CdkDragDrop<any>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const tabs = [...this.form.tabs().value()];
        moveItemInArray(tabs, event.previousIndex, event.currentIndex);
        this.form.setTabs(tabs);
    }

    public editTab(index: number): void {
        this.editableTabIndex.set(index);
        this.currentSettings.set(CurrentSettings.Tab);
    }

    public removeTab(index: number): void {
        if (arrayLengthEq(this.form.tabs().value(), 1)) {
            return;
        }

        this.form.updateTabs((tabs) => {
            return tabs.filter((_, i) => i !== index);
        });
    }

    public async copyTab(index: number): Promise<void> {
        const current = this.form.value();
        const childToCopy = current.children[index] as string;
        const tabs = this.form.tabs().value();

        const childEntity = await firstValueFrom(this.gridsFacade.denormalizedById$(childToCopy));
        const { title } = tabs[index];

        const gridEntity = WebWidgetsFactoryService.copyGrid(childEntity);

        const children = gridEntity.children.map((row) =>
            row.map((widget) => WebWidgetsFactoryService.copyWidgetIntoGrid(widget, gridEntity.id))
        );

        this.widgetsFacade.addMany(children.flat());
        this.gridsFacade.added(
            { ...gridEntity, children: children.map((row) => row.map((w) => w.id)) },
            tabs.length + 1
        );

        this.form.transact(() => {
            this.form.updateChildren((children) => {
                return [...children, gridEntity.id];
            });
            this.form.setTabs([
                ...tabs,
                {
                    title,
                    isActive: false,
                    contentEntityId: gridEntity.id,
                },
            ]);
        });
    }

    public async addTab(): Promise<void> {
        const current = this.form.value();
        const initialChild = current.children[0] as string;
        const tabs = this.form.tabs().value();

        const { props } = await firstValueFrom(this.gridsFacade.denormalizedById$(initialChild));
        const gridEntity = this.gridsFacade.getGridInstanceValue(current.id, props.variant);

        this.gridsFacade.added(gridEntity, tabs.length + 1);

        this.form.transact(() => {
            this.form.updateChildren((children) => {
                return [...children, gridEntity.id];
            });
            this.form.setTabs([
                ...tabs,
                {
                    title: `Tab ${tabs.length + 1}`,
                    isActive: false,
                    contentEntityId: gridEntity.id,
                },
            ]);
        });
    }

    public setSelectedBlockObs(): void {
        const columns = this.columnsEntities();
        const sections = this.sectionsEntities();

        const column = columns?.[this.form.parentId()];
        if (!column) {
            console.warn('Column not found');
            return;
        }

        const section = sections?.[column.parentId];
        if (!section) {
            console.warn('Column not found');
            return;
        }

        this.setSelectedBlock(section.parentId);
    }

    protected readonly controlHandleEvents = EControlHandleEvents;
}
